/**
 * WebhooksSection Component
 *
 * Accordion section for configuring webhooks to call on funnel events.
 * Part of the "DEFINE → USE" pattern: uses defined parameters in webhook requests.
 *
 * Features:
 * - Multiple webhook configurations
 * - Trigger events (screen_leave, onboarding_finish)
 * - HTTP method (GET/POST)
 * - Parameter selection (send all or specific)
 *
 * @module webplacements/components/editors/destinations
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Paper,
  Tooltip,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Collapse,
  Divider,
  Alert,
  FormHelperText,
  CircularProgress,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import WebhookIcon from '@mui/icons-material/Webhook';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import type {
  ParameterDefinition,
  WebhookConfig,
  WebhookTriggerEvent,
  WebhookResponseMapping,
} from '@modules/webplacements/types/webplacements.types';
import { getLocalDevFunnelBaseUrl } from '@modules/webplacements/types/webplacements.types';

/**
 * Screen info for selection dropdown
 */
export interface ScreenOption {
  id: string;
  name: string;
}

export interface WebhooksSectionProps {
  /** All defined parameters from all sources */
  availableParameters: ParameterDefinition[];
  /** Available screens for filtering (optional, for screen-based triggers) */
  availableScreens?: ScreenOption[];
  /** Current webhook configurations */
  webhooks: WebhookConfig[];
  /** Called when webhooks change */
  onChange: (webhooks: WebhookConfig[]) => void;
  /** Whether the editor is disabled */
  disabled?: boolean;
}

const TRIGGER_LABELS: Record<WebhookTriggerEvent, { label: string; description: string }> = {
  screen_show: {
    label: 'Screen Show',
    description: 'Fire-and-forget when screen appears',
  },
  screen_exit: {
    label: 'Screen Exit',
    description: 'Fire-and-forget when user leaves screen',
  },
  screen_enter_await: {
    label: 'Screen Enter (Await)',
    description: 'Wait for response and save to custom variables',
  },
  onboarding_finish: {
    label: 'Funnel Complete',
    description: 'Fire-and-forget when user completes the funnel',
  },
};

const BROWSER_WEBHOOK_TRIGGER_EVENTS: WebhookTriggerEvent[] = [
  'screen_show',
  'screen_exit',
  'onboarding_finish',
];

const isBrowserWebhookTrigger = (trigger: WebhookTriggerEvent): boolean =>
  BROWSER_WEBHOOK_TRIGGER_EVENTS.includes(trigger);

/**
 * Check if trigger type requires screen selection
 */
const triggerRequiresScreenSelection = (trigger: WebhookTriggerEvent): boolean =>
  ['screen_show', 'screen_exit'].includes(trigger);

/**
 * Check if trigger type supports response mapping
 */
const triggerSupportsResponseMapping = (_trigger: WebhookTriggerEvent): boolean =>
  false;

/**
 * Generate a unique webhook ID
 */
const generateWebhookId = (): string =>
  `webhook_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

/**
 * Validate URL format
 */
const isValidUrl = (url: string): boolean => {
  if (!url) return false;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:';
  } catch {
    return false;
  }
};

/**
 * WebhooksSection Component
 *
 * Provides an interface for configuring webhooks with parameter selection.
 */
export const WebhooksSection: React.FC<WebhooksSectionProps> = ({
  availableParameters,
  availableScreens = [],
  webhooks,
  onChange,
  disabled = false,
}) => {
  const theme = useTheme();
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // Test webhook state
  const [testingWebhookId, setTestingWebhookId] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<Record<string, { success: boolean; message: string } | null>>({});

  useEffect(() => {
    const browserWebhooks = webhooks.filter((webhook) =>
      isBrowserWebhookTrigger(webhook.triggerEvent)
    );
    if (browserWebhooks.length !== webhooks.length) {
      onChange(browserWebhooks);
    }
  }, [webhooks, onChange]);

  const toggleExpanded = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  const handleAddWebhook = useCallback(() => {
    const newWebhook: WebhookConfig = {
      id: generateWebhookId(),
      name: 'New Webhook',
      url: '',
      method: 'POST',
      triggerEvent: 'onboarding_finish',
      sendAllParameters: true,
    };

    onChange([...webhooks, newWebhook]);
    setExpandedIds((prev) => new Set([...prev, newWebhook.id]));
  }, [webhooks, onChange]);

  const handleWebhookChange = useCallback(
    (id: string, updates: Partial<WebhookConfig>) => {
      const updated = webhooks.map((wh) => (wh.id === id ? { ...wh, ...updates } : wh));
      onChange(updated);
    },
    [webhooks, onChange]
  );

  const handleDeleteWebhook = useCallback(
    (id: string) => {
      onChange(webhooks.filter((wh) => wh.id !== id));
      setExpandedIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    },
    [webhooks, onChange]
  );

  const handleParameterToggle = useCallback(
    (webhookId: string, paramKey: string, checked: boolean) => {
      const webhook = webhooks.find((wh) => wh.id === webhookId);
      if (!webhook) return;

      const currentKeys = webhook.parameterKeys || [];
      const newKeys = checked
        ? [...currentKeys, paramKey]
        : currentKeys.filter((k) => k !== paramKey);

      handleWebhookChange(webhookId, {
        parameterKeys: newKeys.length > 0 ? newKeys : undefined,
      });
    },
    [webhooks, handleWebhookChange]
  );

  /**
   * Handle screen selection for screen-based triggers
   */
  const handleScreenSelectionChange = useCallback(
    (webhookId: string, screenIds: string[]) => {
      handleWebhookChange(webhookId, {
        triggerScreenIds: screenIds.length > 0 ? screenIds : undefined,
      });
    },
    [handleWebhookChange]
  );

  /**
   * Handle response mapping changes for screen_enter_await
   */
  const handleResponseMappingChange = useCallback(
    (webhookId: string, mappings: WebhookResponseMapping[]) => {
      handleWebhookChange(webhookId, {
        responseMapping: mappings.length > 0 ? mappings : undefined,
      });
    },
    [handleWebhookChange]
  );

  /**
   * Add a new response mapping
   */
  const handleAddResponseMapping = useCallback(
    (webhookId: string) => {
      const webhook = webhooks.find((wh) => wh.id === webhookId);
      if (!webhook) return;

      const newMapping: WebhookResponseMapping = {
        responsePath: '',
        customVariableKey: '',
        dataType: 'string',
      };

      const currentMappings = webhook.responseMapping || [];
      handleResponseMappingChange(webhookId, [...currentMappings, newMapping]);
    },
    [webhooks, handleResponseMappingChange]
  );

  /**
   * Update a response mapping at index
   */
  const handleUpdateResponseMapping = useCallback(
    (webhookId: string, index: number, updates: Partial<WebhookResponseMapping>) => {
      const webhook = webhooks.find((wh) => wh.id === webhookId);
      if (!webhook) return;

      const currentMappings = webhook.responseMapping || [];
      const updatedMappings = currentMappings.map((m, i) =>
        i === index ? { ...m, ...updates } : m
      );
      handleResponseMappingChange(webhookId, updatedMappings);
    },
    [webhooks, handleResponseMappingChange]
  );

  /**
   * Remove a response mapping at index
   */
  const handleRemoveResponseMapping = useCallback(
    (webhookId: string, index: number) => {
      const webhook = webhooks.find((wh) => wh.id === webhookId);
      if (!webhook) return;

      const currentMappings = webhook.responseMapping || [];
      const updatedMappings = currentMappings.filter((_, i) => i !== index);
      handleResponseMappingChange(webhookId, updatedMappings);
    },
    [webhooks, handleResponseMappingChange]
  );

  /**
   * Test webhook by making a direct HTTP request from browser
   * This will reveal CORS issues that would also occur in the actual funnel
   */
  const handleTestWebhook = useCallback(
    async (webhook: WebhookConfig) => {
      setTestingWebhookId(webhook.id);
      setTestResults((prev) => ({ ...prev, [webhook.id]: null }));

      const currentOrigin = window.location.origin;

      try {
        const response = await fetch(webhook.url, {
          method: webhook.method,
          headers: { 'Content-Type': 'application/json' },
          body: webhook.method === 'POST' ? JSON.stringify({ test: true, timestamp: Date.now() }) : undefined,
        });

        if (response.ok) {
          setTestResults((prev) => ({
            ...prev,
            [webhook.id]: {
              success: true,
              message: `✅ Success (HTTP ${response.status}). Test from: ${currentOrigin}`,
            },
          }));
        } else {
          // Try to get response body for more details
          let errorDetail = response.statusText;
          try {
            const body = await response.text();
            if (body && body.length < 200) {
              errorDetail = body;
            }
          } catch {
            // ignore
          }
          setTestResults((prev) => ({
            ...prev,
            [webhook.id]: {
              success: false,
              message: `❌ HTTP ${response.status}: ${errorDetail}. Test from: ${currentOrigin}`,
            },
          }));
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);

        // Detect CORS errors (browsers don't give detailed CORS info for security)
        const isCors =
          errorMessage === 'Failed to fetch' ||
          errorMessage.includes('NetworkError') ||
          errorMessage.includes('Network request failed') ||
          errorMessage.includes('Load failed');

        let detailedMessage: string;
        if (isCors) {
          detailedMessage =
            `🚫 CORS Error: Server ${new URL(webhook.url).hostname} blocks browser requests.\n\n` +
            `• Test origin: ${currentOrigin}\n` +
            `• Target: ${webhook.url}\n\n` +
            `Solution: Server must add Access-Control-Allow-Origin: * or ${currentOrigin} header.\n` +
            `Note: Actual funnel origin will be ${getLocalDevFunnelBaseUrl()} (Edge Gateway) - same CORS issue.`;
        } else {
          detailedMessage = `❌ Error: ${errorMessage}. Test from: ${currentOrigin}`;
        }

        setTestResults((prev) => ({
          ...prev,
          [webhook.id]: {
            success: false,
            message: detailedMessage,
          },
        }));
      } finally {
        setTestingWebhookId(null);
      }
    },
    []
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <WebhookIcon fontSize="small" sx={{ color: theme.palette.secondary.main }} />
        <Typography variant="body2" color="text.secondary">
          Configure webhooks to call when funnel events occur
        </Typography>
        <Tooltip title="Webhooks send HTTP requests to your external systems (CRM, Zapier, etc.) when users interact with the funnel. You can send all or specific parameters.">
          <InfoOutlinedIcon fontSize="small" color="action" />
        </Tooltip>
      </Box>

      {/* Webhooks List */}
      {webhooks.map((webhook, index) => (
        <Paper
          key={webhook.id}
          data-testid={`wp-webhook-row-${index}`}
          variant="outlined"
          sx={{
            mb: 2,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          {/* Webhook Header (collapsed view) */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 1.5,
              cursor: 'pointer',
              '&:hover': { bgcolor: theme.palette.action.hover },
            }}
            onClick={() => toggleExpanded(webhook.id)}
          >
            {expandedIds.has(webhook.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            <Typography variant="subtitle2" sx={{ flex: 1 }}>
              {webhook.name || 'Unnamed Webhook'}
            </Typography>
            <Chip
              label={webhook.method}
              size="small"
              color={webhook.method === 'POST' ? 'primary' : 'default'}
              sx={{ fontSize: '0.7rem' }}
            />
            <Chip
              label={TRIGGER_LABELS[webhook.triggerEvent].label}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.7rem' }}
            />
            <IconButton
              data-testid={`wp-webhook-delete-${index}`}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteWebhook(webhook.id);
              }}
              color="error"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Webhook Details (expanded view) */}
          <Collapse in={expandedIds.has(webhook.id)}>
            <Divider />
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Name */}
              <TextField
                label="Webhook Name"
                value={webhook.name}
                onChange={(e) => handleWebhookChange(webhook.id, { name: e.target.value })}
                size="small"
                fullWidth
                placeholder="CRM Lead Submission"
                disabled={disabled}
              />

              {/* URL with Test Button */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'minmax(0, 1fr) minmax(88px, 104px)',
                  },
                  gap: 1,
                  alignItems: 'start',
                }}
              >
                <TextField
                  label="Webhook URL"
                  value={webhook.url}
                  onChange={(e) => handleWebhookChange(webhook.id, { url: e.target.value })}
                  size="small"
                  fullWidth
                  required
                  placeholder="https://hooks.zapier.com/hooks/catch/123456/abcdef"
                  disabled={disabled}
                  error={webhook.url.length > 0 && !isValidUrl(webhook.url)}
                  helperText={
                    webhook.url.length > 0 && !isValidUrl(webhook.url)
                      ? 'Enter a valid URL (https://... or http://...)'
                      : undefined
                  }
                  InputProps={{
                    sx: { fontFamily: 'monospace', fontSize: '0.875rem' },
                  }}
                  sx={{ minWidth: 0 }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleTestWebhook(webhook)}
                  disabled={disabled || !isValidUrl(webhook.url) || testingWebhookId === webhook.id}
                  sx={{ minWidth: 88, height: 40 }}
                >
                  {testingWebhookId === webhook.id ? <CircularProgress size={20} /> : 'Test'}
                </Button>
              </Box>

              {/* Test Result */}
              {testResults[webhook.id] && (
                <Alert
                  severity={testResults[webhook.id]?.success ? 'success' : 'error'}
                  onClose={() => setTestResults((prev) => ({ ...prev, [webhook.id]: null }))}
                  sx={{ py: 1 }}
                >
                  <Box sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                    {testResults[webhook.id]?.message}
                  </Box>
                </Alert>
              )}

              {/* Method & Trigger */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'minmax(132px, 0.42fr) minmax(220px, 1fr)',
                  },
                  gap: 2,
                  alignItems: 'start',
                }}
              >
                <FormControl size="small" sx={{ minWidth: 0 }}>
                  <InputLabel>Method</InputLabel>
                  <Select
                    value={webhook.method}
                    onChange={(e) =>
                      handleWebhookChange(webhook.id, {
                        method: e.target.value as 'GET' | 'POST',
                      })
                    }
                    label="Method"
                    disabled={disabled}
                    sx={{ height: 40 }}
                  >
                    <MenuItem value="GET">GET</MenuItem>
                    <MenuItem value="POST">POST</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 0 }}>
                  <InputLabel>Trigger Event</InputLabel>
                  <Select
                    value={webhook.triggerEvent}
                    onChange={(e) =>
                      handleWebhookChange(webhook.id, {
                        triggerEvent: e.target.value as WebhookTriggerEvent,
                      })
                    }
                    label="Trigger Event"
                    disabled={disabled}
                    renderValue={(value) =>
                      TRIGGER_LABELS[value as WebhookTriggerEvent]?.label || String(value)
                    }
                    sx={{ height: 40 }}
                  >
                    {BROWSER_WEBHOOK_TRIGGER_EVENTS.map((event) => (
                      <MenuItem key={event} value={event}>
                        <Box>
                          <Typography variant="body2">{TRIGGER_LABELS[event].label}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {TRIGGER_LABELS[event].description}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Screen Selection - for screen-based triggers */}
              {triggerRequiresScreenSelection(webhook.triggerEvent) && (
                <FormControl size="small" fullWidth>
                  <InputLabel>Trigger Screens</InputLabel>
                  <Select
                    multiple
                    value={webhook.triggerScreenIds || []}
                    onChange={(e) => {
                      const value = e.target.value as string[];
                      handleScreenSelectionChange(webhook.id, value.length > 0 ? value : []);
                    }}
                    label="Trigger Screens"
                    disabled={disabled}
                    renderValue={(selected) => {
                      if (selected.length === 0) return 'All screens';
                      const names = selected.map((id) => {
                        const screen = availableScreens.find((s) => s.id === id);
                        return screen?.name || id;
                      });
                      return names.join(', ');
                    }}
                  >
                    {availableScreens.length > 0 ? (
                      availableScreens.map((screen) => (
                        <MenuItem key={screen.id} value={screen.id}>
                          {screen.name || screen.id}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled value="">
                        No screens available - select onboarding first
                      </MenuItem>
                    )}
                  </Select>
                  <FormHelperText>
                    Leave empty to trigger on all screens
                  </FormHelperText>
                </FormControl>
              )}

              <Alert severity="info" sx={{ py: 0.5 }}>
                <Typography variant="caption">
                  Authenticated webhooks require server-side delivery and are not available for browser-side webhooks yet.
                </Typography>
              </Alert>

              {/* Response Mapping - for screen_enter_await only */}
              {triggerSupportsResponseMapping(webhook.triggerEvent) && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Response Mapping
                  </Typography>
                  <Alert severity="info" sx={{ mb: 1, py: 0.5 }}>
                    <Typography variant="caption">
                      Map webhook response fields to custom variables for use in conditions and redirects
                    </Typography>
                  </Alert>

                  {(webhook.responseMapping || []).map((mapping, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        gap: 1,
                        mb: 1,
                        alignItems: 'center',
                      }}
                    >
                      <TextField
                        label="Response Path"
                        value={mapping.responsePath}
                        onChange={(e) =>
                          handleUpdateResponseMapping(webhook.id, index, {
                            responsePath: e.target.value,
                          })
                        }
                        size="small"
                        placeholder="data.score"
                        disabled={disabled}
                        sx={{ flex: 1 }}
                        InputProps={{
                          sx: { fontFamily: 'monospace', fontSize: '0.8rem' },
                        }}
                      />
                      <TextField
                        label="Variable Key"
                        value={mapping.customVariableKey}
                        onChange={(e) =>
                          handleUpdateResponseMapping(webhook.id, index, {
                            customVariableKey: e.target.value,
                          })
                        }
                        size="small"
                        placeholder="leadScore"
                        disabled={disabled}
                        sx={{ flex: 1 }}
                        InputProps={{
                          sx: { fontFamily: 'monospace', fontSize: '0.8rem' },
                        }}
                      />
                      <FormControl size="small" sx={{ minWidth: 100 }}>
                        <InputLabel>Type</InputLabel>
                        <Select
                          value={mapping.dataType}
                          onChange={(e) =>
                            handleUpdateResponseMapping(webhook.id, index, {
                              dataType: e.target.value as 'string' | 'number' | 'boolean',
                            })
                          }
                          label="Type"
                          disabled={disabled}
                        >
                          <MenuItem value="string">String</MenuItem>
                          <MenuItem value="number">Number</MenuItem>
                          <MenuItem value="boolean">Boolean</MenuItem>
                        </Select>
                      </FormControl>
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveResponseMapping(webhook.id, index)}
                        disabled={disabled}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}

                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => handleAddResponseMapping(webhook.id)}
                    disabled={disabled}
                    sx={{ mt: 1 }}
                  >
                    Add Mapping
                  </Button>
                </Box>
              )}

              {/* Parameter Selection */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Parameters to Send
                </Typography>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={webhook.sendAllParameters ?? true}
                      onChange={(e) =>
                        handleWebhookChange(webhook.id, {
                          sendAllParameters: e.target.checked,
                          parameterKeys: e.target.checked ? undefined : [],
                        })
                      }
                      size="small"
                      disabled={disabled}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      Send all defined parameters
                    </Typography>
                  }
                />

                {!webhook.sendAllParameters && (
                  <Box
                    sx={{
                      mt: 1,
                      p: 1.5,
                      bgcolor: theme.palette.action.hover,
                      borderRadius: 1,
                    }}
                  >
                    {availableParameters.length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {availableParameters.map((param) => (
                          <FormControlLabel
                            key={param.key}
                            control={
                              <Checkbox
                                checked={webhook.parameterKeys?.includes(param.key) ?? false}
                                onChange={(e) =>
                                  handleParameterToggle(webhook.id, param.key, e.target.checked)
                                }
                                size="small"
                                disabled={disabled}
                              />
                            }
                            label={
                              <Chip
                                label={param.key}
                                size="small"
                                sx={{
                                  fontFamily: 'monospace',
                                  fontSize: '0.75rem',
                                  bgcolor: theme.palette.action.selected,
                                }}
                              />
                            }
                          />
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        No parameters defined yet. Add parameters in the sections above.
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>

              {/* Preview Request */}
              {webhook.url && (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    bgcolor: theme.palette.action.hover,
                    borderStyle: 'dashed',
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    gutterBottom
                  >
                    Request preview:
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '0.7rem',
                      m: 0,
                      p: 1,
                      bgcolor: theme.palette.background.default,
                      borderRadius: 1,
                      overflow: 'auto',
                    }}
                  >
                    {`${webhook.method} ${webhook.url}\n`}
                    {webhook.method === 'POST' && `Content-Type: application/json\n\n`}
                    {webhook.method === 'POST' &&
                      JSON.stringify(
                        {
                          event: { name: webhook.triggerEvent, timestamp: Date.now() },
                          data:
                            webhook.sendAllParameters || !webhook.parameterKeys
                              ? Object.fromEntries(
                                  availableParameters.map((p) => [p.key, 'sample_value'])
                                )
                              : Object.fromEntries(
                                  (webhook.parameterKeys || []).map((k) => [k, 'sample_value'])
                                ),
                        },
                        null,
                        2
                      )}
                  </Box>
                </Paper>
              )}
            </Box>
          </Collapse>
        </Paper>
      ))}

      {/* Add Webhook Button */}
      <Button
        data-testid="wp-webhook-add-btn"
        size="small"
        startIcon={<AddIcon />}
        onClick={handleAddWebhook}
        disabled={disabled}
      >
        Add Webhook
      </Button>

      {/* Empty State */}
      {webhooks.length === 0 && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            textAlign: 'center',
            bgcolor: theme.palette.action.hover,
            borderRadius: 1,
            border: `1px dashed ${theme.palette.divider}`,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No webhooks configured yet.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Add webhooks to send data to external systems (CRM, Zapier, etc.)
          </Typography>
        </Box>
      )}

      {/* Info Box */}
      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="caption">
          Webhooks are non-blocking - errors won't affect funnel navigation. Failed requests are
          retried once with exponential backoff.
        </Typography>
      </Alert>
    </Box>
  );
};

export default WebhooksSection;
